package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.PayrollRecordDto

@Composable
internal fun PayrollYearView(
    year: Int,
    records: List<PayrollRecordDto>,
    isMobile: Boolean,
    isNarrow: Boolean,
    onMonthClick: (anchor: String) -> Unit
) {
    val byMonth = remember(records) {
        records.groupBy { it.day.substring(5, 7).toInt() }
    }

    // Current month identifier
    val currentYearMonth = remember {
        val d = kotlin.js.Date()
        "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}"
    }

    // Annual KPI aggregations
    val totalAnnualMinutes = remember(records) { records.sumOf { it.netMinutes } }
    val totalAnnualHours = totalAnnualMinutes / 60.0
    val activeMonthsCount = byMonth.count { it.value.isNotEmpty() }
    val avgHoursPerActiveMonth = if (activeMonthsCount > 0) totalAnnualHours / activeMonthsCount else 0.0

    val maxMonthEntry = remember(byMonth) {
        byMonth.entries.maxByOrNull { entry -> entry.value.sumOf { it.netMinutes } }
    }
    val maxMonthHours = (maxMonthEntry?.value?.sumOf { it.netMinutes } ?: 0) / 60.0
    val peakMonthName = if (maxMonthHours > 0 && maxMonthEntry != null) {
        MONTH_NAMES[maxMonthEntry.key - 1].replaceFirstChar { it.uppercase() }
    } else "–"

    val uniqueStaffAnnual = remember(records) { records.map { it.userId }.distinct().size }
    val approvedRatio = if (records.isNotEmpty()) (records.count { it.approved } * 100 / records.size) else 100

    val cols = when {
        isMobile -> 2
        isNarrow -> 3
        else -> 4
    }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {

        // ── Top KPI Stats Ribbon ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", if (isMobile) "repeat(2, 1fr)" else "repeat(auto-fit, minmax(180px, 1fr))")
                property("gap", "0.75rem")
            }
        }) {
            // KPI 1: Årstotalt
            AnnualKpiCard(
                icon = "fa-clock",
                iconBg = "var(--rp-info-bg)",
                iconColor = "var(--rp-pine)",
                title = "ÅRSTOTALT",
                value = "${((totalAnnualHours * 10).toInt() / 10.0)}h",
                subtext = "${records.size} arbetade pass"
            )

            // KPI 2: Snitt per månad
            AnnualKpiCard(
                icon = "fa-chart-line",
                iconBg = "var(--rp-info-bg)",
                iconColor = "var(--rp-pine)",
                title = "SNITT / MÅNAD",
                value = "${((avgHoursPerActiveMonth * 10).toInt() / 10.0)}h",
                subtext = "Över $activeMonthsCount aktiva mån"
            )

            // KPI 3: Toppmånad
            AnnualKpiCard(
                icon = "fa-fire",
                iconBg = "var(--rp-warn-bg)",
                iconColor = "var(--rp-warn-text)",
                title = "TOPPMÅNAD",
                value = if (maxMonthHours > 0) "$peakMonthName" else "–",
                subtext = if (maxMonthHours > 0) "${maxMonthHours.toInt()}h högsta volym" else "Ingen aktivitet"
            )

            // KPI 4: Aktiv personal
            AnnualKpiCard(
                icon = "fa-users",
                iconBg = "var(--rp-info-bg)",
                iconColor = "var(--rp-iris)",
                title = "AKTIV PERSONAL",
                value = "$uniqueStaffAnnual pers",
                subtext = "$approvedRatio% godkänd tid"
            )
        }

        // ── 12-Month Grid ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat($cols, 1fr)")
                property("gap", if (isMobile) "0.75rem" else "1rem")
            }
        }) {
            for (m in 1..12) {
                val monthRecs = byMonth[m] ?: emptyList()
                val totalMin = monthRecs.sumOf { it.netMinutes }
                val totalH = totalMin / 60.0
                val staffCount = monthRecs.map { it.userId }.distinct().size
                val approvedCount = monthRecs.count { it.approved }
                val fillPct = if (maxMonthHours > 0) (totalH / maxMonthHours * 100.0).coerceIn(0.0, 100.0) else 0.0
                val monthStr = m.toString().padStart(2, '0')
                val anchor = "$year-$monthStr-01"
                val isCurrentMonth = "$year-$monthStr" == currentYearMonth
                val quarter = "Q${(m - 1) / 3 + 1}"

                Div({
                    onClick { onMonthClick(anchor) }
                    style {
                        property("background", if (isCurrentMonth) "var(--rp-surface)" else "var(--rp-surface)")
                        property("border-radius", "16px")
                        property("padding", "1.15rem 1.25rem")
                        property("cursor", "pointer")
                        property("border", if (isCurrentMonth) "1.5px solid var(--rp-pine)" else "1px solid var(--rp-hl-med)")
                        property("box-shadow", if (isCurrentMonth) "0 4px 12px rgba(79,70,229,0.08)" else "0 1px 3px rgba(0,0,0,0.02)")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("justify-content", "space-between")
                        property("transition", "all 0.15s ease")
                    }
                }) {
                    // Card Top: Month name + Quarter tag or Current Badge
                    Div {
                        Div({
                            style {
                                property("display", "flex")
                                property("justify-content", "space-between")
                                property("align-items", "center")
                                property("margin-bottom", "0.65rem")
                            }
                        }) {
                            Span({
                                style {
                                    property("font-weight", "800")
                                    property("font-size", "0.95rem")
                                    property("color", if (isCurrentMonth) "var(--rp-pine)" else "var(--rp-text)")
                                }
                            }) {
                                Text(MONTH_NAMES[m - 1].replaceFirstChar { it.uppercase() })
                            }

                            if (isCurrentMonth) {
                                Span({
                                    style {
                                        property("background", "var(--rp-info-bg)")
                                        property("color", "var(--rp-pine)")
                                        property("padding", "0.15rem 0.5rem")
                                        property("border-radius", "50px")
                                        property("font-size", "0.68rem")
                                        property("font-weight", "800")
                                    }
                                }) { Text("Aktiv månad") }
                            } else {
                                Span({
                                    style {
                                        property("background", "var(--rp-overlay)")
                                        property("color", "var(--rp-subtle)")
                                        property("padding", "0.15rem 0.45rem")
                                        property("border-radius", "6px")
                                        property("font-size", "0.68rem")
                                        property("font-weight", "700")
                                    }
                                }) { Text(quarter) }
                            }
                        }

                        // Hours Metric Display
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "baseline")
                                property("gap", "0.35rem")
                                property("margin-bottom", "0.35rem")
                            }
                        }) {
                            val h = totalH.toInt()
                            val minLeft = ((totalH - h) * 60).toInt()
                            Span({
                                style {
                                    property("font-size", "1.5rem")
                                    property("font-weight", "800")
                                    property("color", if (totalH > 0) "var(--rp-text)" else "var(--rp-muted)")
                                    property("letter-spacing", "-0.5px")
                                }
                            }) {
                                Text("${h}h${if (minLeft > 0) " ${minLeft}m" else ""}")
                            }
                        }

                        // Secondary Meta: Staff count & Approved pass
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.6rem")
                                property("margin-bottom", "0.65rem")
                                property("font-size", "0.75rem")
                                property("color", "var(--rp-subtle)")
                                property("font-weight", "600")
                            }
                        }) {
                            Span({ style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.25rem") } }) {
                                FaIcon("fa-solid fa-users", size = "0.68rem", color = "var(--rp-muted)")
                                Text("$staffCount pers")
                            }
                            if (monthRecs.isNotEmpty()) {
                                Span({ style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.25rem"); property("color", if (approvedCount == monthRecs.size) "var(--rp-ok-text)" else "var(--rp-subtle)") } }) {
                                    FaIcon("fa-solid fa-check", size = "0.68rem", color = if (approvedCount == monthRecs.size) "var(--rp-ok-text)" else "var(--rp-muted)")
                                    Text("$approvedCount pass")
                                }
                            }
                        }

                        // Proportional Volume Bar
                        Div({
                            style {
                                property("background", "var(--rp-overlay)")
                                property("border-radius", "4px")
                                property("height", "5px")
                                property("overflow", "hidden")
                                property("margin-bottom", "0.75rem")
                            }
                        }) {
                            Div({
                                style {
                                    property("height", "100%")
                                    property("border-radius", "4px")
                                    property("width", "$fillPct%")
                                    property("background", if (isCurrentMonth) "var(--rp-pine)" else (if (totalH > 0) "var(--rp-pine)" else "var(--rp-hl-med)"))
                                    property("transition", "width 0.3s ease")
                                }
                            }) {}
                        }
                    }

                    // Card Footer: Clean Open link with arrow
                    Div({
                        style {
                            property("border-top", "1px solid var(--rp-hl-med)")
                            property("padding-top", "0.55rem")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "flex-end")
                            property("gap", "0.3rem")
                            property("font-size", "0.74rem")
                            property("font-weight", "700")
                            property("color", if (isCurrentMonth) "var(--rp-pine)" else "var(--rp-subtle)")
                        }
                    }) {
                        Text("Öppna månad")
                        FaIcon("fa-solid fa-arrow-right", size = "0.65rem", color = if (isCurrentMonth) "var(--rp-pine)" else "var(--rp-muted)")
                    }
                }
            }
        }
    }
}

@Composable
private fun AnnualKpiCard(
    icon: String,
    iconBg: String,
    iconColor: String,
    title: String,
    value: String,
    subtext: String
) {
    Div({
        style {
            property("background", "var(--rp-surface)")
            property("border-radius", "14px")
            property("padding", "0.9rem 1.1rem")
            property("border", "1px solid var(--rp-hl-med)")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.02)")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "0.85rem")
        }
    }) {
        Div({
            style {
                property("width", "38px")
                property("height", "38px")
                property("border-radius", "10px")
                property("background", iconBg)
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
            }
        }) {
            FaIcon("fa-solid $icon", size = "0.95rem", color = iconColor)
        }
        Div {
            Span({
                style {
                    property("font-size", "0.65rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-subtle)")
                    property("letter-spacing", "0.5px")
                    property("display", "block")
                }
            }) { Text(title) }
            Div({
                style {
                    property("font-size", "1.15rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-text)")
                    property("margin", "0.05rem 0")
                }
            }) { Text(value) }
            Span({
                style {
                    property("font-size", "0.72rem")
                    property("font-weight", "600")
                    property("color", "var(--rp-subtle)")
                }
            }) { Text(subtext) }
        }
    }
}
